import HttpClient from 'gateway/HttpClient';
import { UserClient } from './UserClient';
import HttpMethod from '../../constant/HttpMethod';
import { KeycloakUserMethods, UserMethods } from '../../constant/MethodNames';
import { User, UserPreferences, UserSystemAttributes } from '../../domain/rbac/RbacDomains';
import { notUndefined } from '../../util/Assert';
import { query } from '@trlogic/client-js';
import Query = query.Query;

class NestUserClient implements UserClient {
  private readonly httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    notUndefined(httpClient, 'Http client cannot be undefined');
    this.httpClient = httpClient;
  }

  public login(payload: any): Promise<any> {
    return this.httpClient.execute(HttpMethod.POST, KeycloakUserMethods.LOGIN, payload);
  }

  public logout(): Promise<any> {
    return this.httpClient.execute(HttpMethod.POST, KeycloakUserMethods.LOGOUT);
  }

  public delete(id: string): Promise<any> {
    return this.httpClient.execute(HttpMethod.DELETE, `${UserMethods.REMOVE_ONE}/${id}`);
  }

  public async create(user: User, password: string): Promise<void> {
    const dto = { id: user.username, user, password: password.replace(' ', '') };
    await this.httpClient.execute(HttpMethod.POST, KeycloakUserMethods.SETUP_CREATE, dto).catch((e) => e);
  }

  public update(user: User, password: string): Promise<any> {
    const dto = { user, password: password?.replace(' ', '') };
    return this.httpClient.execute(HttpMethod.PUT, KeycloakUserMethods.SETUP_UPDATE, dto);
  }

  public resetPassword(currentPassword: string, newPassword: string): Promise<void> {
    const dto = { oldPassword: currentPassword, password: newPassword };
    return this.httpClient.execute(HttpMethod.PUT, KeycloakUserMethods.RESET_PASSWORD, dto);
  }

  public profileUpdate(id: string, user: User): Promise<void> {
    const dto = { id, user };
    return this.httpClient.execute(HttpMethod.PUT, KeycloakUserMethods.PROFILE_UPDATE, dto);
  }

  public updatePreferences(preferences: UserPreferences): Promise<void> {
    const dto = { preferences };
    return this.httpClient.execute(HttpMethod.PUT, KeycloakUserMethods.UPDATE_PREFERENCES, dto);
  }

  public getCount(query: Query): Promise<any> {
    return this.httpClient.execute(HttpMethod.GET, `${UserMethods.COUNT}`, undefined, { query });
  }

  public exist(username: string, email: string): Promise<any> {
    return this.httpClient.execute(HttpMethod.GET, `${UserMethods.EXIST}`, {}, { username: username, email: email });
  }

  public getQRData(): Promise<Record<string, any>> {
    return this.httpClient.execute(HttpMethod.GET, `${UserMethods.GET_QR}`);
  }

  public getUsernames(): Promise<{ username: string; fullName: string }[]> {
    return this.httpClient.execute(HttpMethod.GET, UserMethods.GET_USERNAMES);
  }

  public updateAttributes(attributes: UserSystemAttributes): Promise<void> {
    const dto = { attributes };
    return this.httpClient.execute(HttpMethod.PUT, UserMethods.UPDATE_ATTRIBUTES, dto);
  }

  public changeActiveRiskType(): Promise<void> {
    return this.httpClient.execute(HttpMethod.POST, UserMethods.CHANGE_ACTIVE_RISK_TYPE);
  }

  public readErrorLoginNotification(): Promise<void> {
    return this.httpClient.execute(HttpMethod.POST, UserMethods.READ_ERROR_LOGIN_NOTIFICATION);
  }

  public updateToken(token: string): Promise<void> {
    return this.httpClient.execute(HttpMethod.POST, UserMethods.UPDATE_TOKEN, undefined, undefined, { authorization: `Bearer ${token}` });
  }
}

export default NestUserClient;
